/**
 * App Guide Page — Matches Xamarin AppGuidePage.xaml (phone layout)
 *
 * Displays a carousel of guide slides with:
 * - Position counter ("1/7")
 * - Background image per slide (AspectFit)
 * - Header text
 * - Description text
 * - Left/Right navigation arrows + swipe gestures
 * - Skip/Continue button
 *
 * Data source: Config setting APP_GUIDE_URL (JSON array of {image, header, text}) from verify-otp response
 * ViewModel: AppGuideViewModel.cs
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IMAGE_CONSTANTS, ROUTES } from '@/constants';
import { useAppSelector, selectAppGuideSlides, selectAppGuideSlidesSpanish } from '@/store';

import type { AppGuideSlide } from '@/types';

// ─────────────────────────────────────────────────────────────────
// Page Component — Matches Xamarin AppGuideViewModel
// ─────────────────────────────────────────────────────────────────

export function AppGuidePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const englishSlides = useAppSelector(selectAppGuideSlides);
  const spanishSlides = useAppSelector(selectAppGuideSlidesSpanish);
  const slides = i18n.language === 'es' && spanishSlides.length > 0 ? spanishSlides : englishSlides;

  const [position, setPosition] = useState(1);

  const currentItem: AppGuideSlide | undefined = slides[position - 1];
  const isNextAvailable = position < slides.length;

  // Matches Xamarin: SkipGuideCommand → NavigateAsync to DashboardPage
  const handleSkip = useCallback(() => {
    navigate(ROUTES.dashboard);
  }, [navigate]);

  // Matches Xamarin: NextImageCommand
  const handleNext = useCallback(() => {
    if (position < slides.length) {
      setPosition((p) => p + 1);
    }
  }, [position, slides.length]);

  // Matches Xamarin: PreviousImageCommand
  const handlePrevious = useCallback(() => {
    if (position > 1) {
      setPosition((p) => p - 1);
    }
  }, [position]);

  // Swipe handling — Matches Xamarin SwipeGestureRecognizer
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.touches[0]?.clientX ?? null);
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX === null) return;
      const touchEndX = e.changedTouches[0]?.clientX ?? touchStartX;
      const diff = touchStartX - touchEndX;
      if (diff > 50) {
        handleNext(); // Swipe left → next
      } else if (diff < -50) {
        handlePrevious(); // Swipe right → previous
      }
      setTouchStartX(null);
    },
    [touchStartX, handleNext, handlePrevious]
  );

  // No slides loaded yet
  if (slides.length === 0 || !currentItem) {
    return null;
  }

  return (
    // Matches Xamarin: Grid Padding="10,5", fills available height
    <div className="flex h-[calc(100dvh-3.5rem)] flex-col overflow-hidden px-[10px] py-[5px]">
      {/* Carousel area — fills available height
          Matches Xamarin: Grid.ColumnDefinitions=".05*,*,.05*" */}
      <div className="grid min-h-0 flex-1 grid-cols-[.05fr_1fr_.05fr]">
        {/* Column 0: Left arrow — Matches Xamarin: appguide_scroll_arrow.svg with RotationY=180
            Hidden when Position == 1 via DataTrigger. HeightRequest=50 */}
        <div className="flex items-center justify-center self-center">
          {position > 1 && (
            <button onClick={handlePrevious} className="p-2">
              <img
                src={IMAGE_CONSTANTS.appGuideScrollArrow}
                alt="Previous"
                className="h-[50px] rotate-180"
              />
            </button>
          )}
        </div>

        {/* Column 1: Center content — vertically centered image + text group */}
        <div
          className="flex min-h-0 flex-col items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Position counter — Matches Xamarin: FontSize=16(Phone), PoppinsRegular, TextColor=#003366 */}
          <span className="mb-[10px] shrink-0 font-poppins text-[16px] text-[#003366]">
            {position}/{slides.length}
          </span>

          {/* Slide image — capped at 60% of container height, AspectFit */}
          <img
            src={currentItem.image}
            alt={currentItem.header}
            className="max-h-[60%] max-w-full shrink-0 object-contain"
          />

          {/* Header + Description — Matches Xamarin: StackLayout centered */}
          <div className="mt-[10px] flex shrink-0 flex-col items-center gap-[5px]">
            {/* Header — Matches Xamarin: FontSize=16(Phone), PoppinsRegular, TextColor=#003366 */}
            <span className="text-center font-poppins text-[18px] font-medium text-[#003366]">
              {currentItem.header}
            </span>
            {/* Description — Matches Xamarin: FontSize=12(Phone), PoppinsRegular,
                TextColor=#78899B (AppGuideTextColor), HorizontalTextAlignment=Center, WordWrap */}
            <p className="text-center font-poppins text-[16px] leading-relaxed text-[#78899B]">
              {currentItem.text}
            </p>
          </div>
        </div>

        {/* Column 2: Right arrow — Matches Xamarin: appguide_scroll_arrow.svg
            Hidden when IsNextAvailable is False. HeightRequest=50 */}
        <div className="flex items-center justify-center self-center">
          {isNextAvailable && (
            <button onClick={handleNext} className="p-2">
              <img src={IMAGE_CONSTANTS.appGuideScrollArrow} alt="Next" className="h-[50px]" />
            </button>
          )}
        </div>
      </div>

      {/* Skip/Continue button at bottom-right
          Matches Xamarin: HorizontalOptions=End, FontFamily=PoppinsRegular, FontSize=12,
          WidthRequest=100, HeightRequest=35, BackgroundColor=Transparent,
          CornerRadius=8, BorderColor=Black, BorderWidth=1 */}
      <div className="flex shrink-0 justify-end py-2">
        <button
          onClick={handleSkip}
          className="h-[35px] w-[100px] rounded-[8px] border border-black bg-transparent font-poppins text-[12px]"
        >
          {isNextAvailable ? t('appGuide.skip') : t('appGuide.continue')}
        </button>
      </div>
    </div>
  );
}
